import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Paper,
  Box,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useSkills } from '../../contexts/SkillsContext';


const TechniciansList = ({ onEdit, onDelete, hasPermission }) => {
  const { technicians, loading: techLoading, error: techError } = useTechnicians();
  const { skills, loading: skillsLoading } = useSkills();

  const [searchQuery, setSearchQuery] = useState('');

  // Créer une map des compétences pour un accès rapide
  const skillsMap = React.useMemo(() => {
    const map = {};
    skills.forEach(skill => {
      map[skill.id] = skill;
    });
    return map;
  }, [skills]);

  const loading = techLoading || skillsLoading;

  const handleDelete = (technician) => {
    if (onDelete) {
      onDelete(technician);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const filteredTechnicians = technicians.filter(technician => {
    const fullName = `${technician.firstName} ${technician.lastName}`.toLowerCase();
    const searchTerms = searchQuery.toLowerCase().split(' ');
    
    // Vérifier si tous les termes de recherche sont présents
    return searchTerms.every(term => {
      // Recherche dans le nom complet
      if (fullName.includes(term)) return true;
      
      // Recherche dans les compétences (support nouveau format avec niveaux)
      if (technician.skills?.some(skillItem => {
        const skillId = typeof skillItem === 'object' ? skillItem.skillId : skillItem;
        const skill = skillsMap[skillId];
        return skill && skill.name.toLowerCase().includes(term);
      })) return true;

      // Recherche dans l'email
      if (technician.email.toLowerCase().includes(term)) return true;

      return false;
    });
  });

  if (loading) {
    return (
      <Paper sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (techError) {
    return (
      <Paper sx={{ p: 2 }}>
        <Alert severity="error">
          Erreur lors du chargement des techniciens: {techError}
        </Alert>
      </Paper>
    );
  }

  if (technicians.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="body1" color="textSecondary">
          Aucun technicien n'a été ajouté.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par nom, prénom, email ou compétence..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          size="small"
        />
      </Paper>

      <Paper>
        <List>
          {filteredTechnicians.length === 0 ? (
            <ListItem>
              <Typography variant="body1" color="textSecondary">
                Aucun résultat ne correspond à votre recherche.
              </Typography>
            </ListItem>
          ) : (
            filteredTechnicians.map((technician) => (
              <ListItem key={technician.id} divider>
                <Box sx={{ flexGrow: 1 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" component="span">
                          {`${technician.firstName} ${technician.lastName}`}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span" display="block">
                          {technician.email} • {technician.phone}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {technician.skills
                            ?.map((skillItem) => {
                              // Support nouveau format avec niveaux
                              const skillId = typeof skillItem === 'object' ? skillItem.skillId : skillItem;
                              const level = typeof skillItem === 'object' ? skillItem.level : 1;
                              const skill = skillsMap[skillId];
                              
                              return skill ? { ...skill, level } : null;
                            })
                            .filter(Boolean)
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((skill) => (
                              <Chip
                                key={skill.id}
                                label={`${skill.name} - N${skill.level}`}
                                size="small"
                                sx={{
                                  backgroundColor: `${skill.color}20`,
                                  border: `1px solid ${skill.color}40`,
                                  color: 'text.primary',
                                  '& .MuiChip-label': {
                                    color: 'inherit'
                                  }
                                }}
                              />
                            ))}
                        </Box>
                      </>
                    }
                  />
                </Box>
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      edge="end"
                      aria-label="modifier"
                      onClick={() => onEdit(technician)}
                      disabled={!hasPermission?.('edit')}
                      title={!hasPermission?.('edit') ? 'Vous n\'avez pas la permission de modifier ce technicien' : ''}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="supprimer"
                      onClick={() => onDelete(technician)}
                      disabled={!hasPermission?.('delete')}
                      title={!hasPermission?.('delete') ? 'Vous n\'avez pas la permission de supprimer ce technicien' : ''}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))
          )}
        </List>
      </Paper>
    </Box>
  );
};

export default TechniciansList; 